import { DrawCustomModeThis, DrawFeature, DrawPoint } from "@mapbox/mapbox-gl-draw";

import { EditingLayer } from "fond/types";

import { NodeTopologyConfig, pullInFeatures, refreshSnappedStatus, splitPaths } from "../topologyHelper";

import { aerialConfig } from "./helper";

/**
 * Makes the onCreate callback for poles.
 *
 * Handles:
 * 1. If placed on an existing pole → delete the new one (dedup)
 * 2. If placed on a span → split the span at the pole
 * 3. Refreshes snapped status of the new pole
 * 4. Fires draw.create event
 */
export const makeOnCreateAerialPole = (config: NodeTopologyConfig): NonNullable<EditingLayer["onCreate"]> =>
  function onCreateAerialPole(this: DrawCustomModeThis, feature: DrawFeature, state: Record<string, unknown>): void {
    const pole = feature as DrawPoint;
    pole.setProperty("layerId", config.pointLayerId);

    // Get collocated spans and poles
    const { lines: spans, points: poles } = pullInFeatures.call(this, config, pole);

    if (poles.length > 0) {
      // If pole is created on top of another delete the newly created pole
      this.deleteFeature(pole.id as string, { silent: true });
      return;
    } else if (spans.length > 0 && config.lineLayerId) {
      // If pole is created on top of a span, we want to split the path into two segments
      // and create a new pole at the snapped location.
      // Multiple spans can exist at a T-intersection; we must attempt to split all of them.
      const spanConfig = { ...config, lineLayerId: config.lineLayerId };
      spans.forEach((span) => splitPaths.call(this, spanConfig, span, pole));
    }

    // We need to refresh the snapped status of the feature
    // to ensure that it is correctly marked as (un)snapped.
    refreshSnappedStatus.call(this, config, pole);

    // Fire draw.create synchronously (splitPaths deletes synchronously) so that it's
    // captured in history before any mode transition.
    this.map.fire("draw.create", { features: [pole.toGeoJSON()] });
  };

/**
 * Pole onCreate callback bound to the planner span/pole layers.
 */
export const onCreateAerialPole = makeOnCreateAerialPole(aerialConfig);
